import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Ticket, Calendar, MapPin, Clock, ShieldCheck, CreditCard,
  Lock, ArrowLeft, Check, Loader2, User, Mail, KeyRound
} from 'lucide-react';
import { format } from 'date-fns';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      fontFamily: 'system-ui, sans-serif',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  try {
    return format(new Date(dateStr + 'T12:00:00'), 'EEE, MMM d, yyyy');
  } catch { return dateStr; }
};

// Inner checkout form (needs Stripe context)
const CheckoutForm = ({ item, itemType, quantity, totalPrice, serviceFee, faceValue, addons: availableAddons }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { isAuthenticated, user, login, register } = useAuth();

  const [authMode, setAuthMode] = useState('register');
  const [guestInfo, setGuestInfo] = useState({ full_name: '', email: '', password: '' });
  const [loginInfo, setLoginInfo] = useState({ email: '', password: '' });
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [step, setStep] = useState(isAuthenticated ? 'payment' : 'account');
  const [success, setSuccess] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);

  const addonTotal = selectedAddons.reduce((sum, a) => sum + (a.price || 0), 0);
  const finalTotal = totalPrice + addonTotal;

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => prev.find(a => a.id === addon.id) ? prev.filter(a => a.id !== addon.id) : [...prev, addon]);
  };

  useEffect(() => {
    if (isAuthenticated) setStep('payment');
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await login(loginInfo.email, loginInfo.password);
      toast.success('Signed in!');
      setStep('payment');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleRegisterAndPay = async () => {
    // Validate
    if (!isAuthenticated) {
      if (authMode === 'register') {
        if (!guestInfo.full_name || !guestInfo.email || !guestInfo.password) {
          toast.error('Please fill in all fields');
          return;
        }
        if (guestInfo.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
      }
    }

    if (!cardComplete) {
      toast.error('Please enter your card details');
      return;
    }

    setProcessing(true);
    try {
      // Step 1: Register if needed
      let token = null;
      if (!isAuthenticated) {
        const regResponse = await register(
          guestInfo.email,
          guestInfo.password,
          guestInfo.full_name,
          'consumer'
        );
        token = regResponse?.token;
      }

      // Step 2: Create checkout session (now authenticated)
      const checkoutResponse = await api.post('/payments/checkout', {
        item_type: itemType,
        item_id: item.id,
        quantity: quantity,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe checkout
      if (checkoutResponse.data?.url) {
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('already exists') || detail?.includes('already registered')) {
        toast.error('An account with this email already exists. Please sign in instead.');
        setAuthMode('login');
        setLoginInfo({ email: guestInfo.email, password: '' });
      } else {
        toast.error(detail || 'Checkout failed. Please try again.');
      }
      setProcessing(false);
    }
  };

  const handleAuthenticatedPay = async () => {
    if (!cardComplete) {
      toast.error('Please enter your card details');
      return;
    }
    setProcessing(true);
    try {
      const checkoutResponse = await api.post('/payments/checkout', {
        item_type: itemType,
        item_id: item.id,
        quantity: quantity,
        origin_url: window.location.origin,
      });
      if (checkoutResponse.data?.url) {
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
        <p className="text-muted-foreground mb-6">Your tickets have been confirmed.</p>
        <Button onClick={() => navigate('/dashboard')}>View My Tickets</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-3 text-sm">
        <div className={`flex items-center gap-1.5 ${step === 'account' ? 'text-indigo-600 font-semibold' : 'text-green-600'}`}>
          {isAuthenticated ? <Check className="w-4 h-4" /> : <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">1</span>}
          Account
        </div>
        <div className="w-8 h-px bg-border" />
        <div className={`flex items-center gap-1.5 ${step === 'payment' ? 'text-indigo-600 font-semibold' : 'text-muted-foreground'}`}>
          <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${step === 'payment' ? 'bg-indigo-600 text-white' : 'bg-muted text-muted-foreground'}`}>2</span>
          Payment
        </div>
      </div>

      {/* Account Step (only for non-authenticated) */}
      {!isAuthenticated && step === 'account' && (
        <Card>
          <CardContent className="p-6 space-y-4">
            {authMode === 'register' ? (
              <>
                <div>
                  <h3 className="font-semibold text-lg">Create your account</h3>
                  <p className="text-sm text-muted-foreground">Quick signup to complete your purchase</p>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="guest-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="guest-name" placeholder="John Doe" value={guestInfo.full_name} onChange={(e) => setGuestInfo({...guestInfo, full_name: e.target.value})} className="pl-10" data-testid="checkout-name-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="guest-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="guest-email" type="email" placeholder="you@example.com" value={guestInfo.email} onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})} className="pl-10" data-testid="checkout-email-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="guest-password">Create Password</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="guest-password" type="password" placeholder="Min 6 characters" value={guestInfo.password} onChange={(e) => setGuestInfo({...guestInfo, password: e.target.value})} className="pl-10" data-testid="checkout-password-input" />
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => setStep('payment')} disabled={!guestInfo.full_name || !guestInfo.email || !guestInfo.password || guestInfo.password.length < 6} data-testid="checkout-continue-btn">
                  Continue to Payment
                </Button>
                <p className="text-sm text-center text-muted-foreground">
                  Already have an account?{' '}
                  <button className="text-indigo-600 hover:underline font-medium" onClick={() => setAuthMode('login')} data-testid="checkout-signin-link">
                    Sign in
                  </button>
                </p>
              </>
            ) : (
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <h3 className="font-semibold text-lg">Welcome back</h3>
                  <p className="text-sm text-muted-foreground">Sign in to complete your purchase</p>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" placeholder="you@example.com" value={loginInfo.email} onChange={(e) => setLoginInfo({...loginInfo, email: e.target.value})} required data-testid="checkout-login-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Password</Label>
                    <Input type="password" placeholder="••••••••" value={loginInfo.password} onChange={(e) => setLoginInfo({...loginInfo, password: e.target.value})} required data-testid="checkout-login-password" />
                  </div>
                </div>
                <Button type="submit" className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700" disabled={processing} data-testid="checkout-login-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Sign In & Continue
                </Button>
                <p className="text-sm text-center text-muted-foreground mt-3">
                  New here?{' '}
                  <button className="text-indigo-600 hover:underline font-medium" onClick={() => setAuthMode('register')} type="button">
                    Create an account
                  </button>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Authenticated user info */}
      {isAuthenticated && (
        <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
          <Check className="w-5 h-5 text-green-600" />
          <div>
            <p className="font-medium text-sm">Purchasing as {user?.full_name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>
      )}

      {/* Payment Step */}
      {step === 'payment' && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                Payment Details
              </h3>
              <p className="text-sm text-muted-foreground">Your payment is secured with SSL encryption</p>
            </div>

            <div className="border rounded-lg p-4 bg-white dark:bg-gray-950">
              <CardElement options={cardElementOptions} onChange={(e) => setCardComplete(e.complete)} />
            </div>

            {/* Add-ons */}
            {availableAddons?.length > 0 && (
              <div className="space-y-2 border-t pt-4">
                <p className="text-sm font-semibold">Enhance Your Experience</p>
                {availableAddons.map(addon => (
                  <div key={addon.id} className="flex items-center justify-between p-2 border rounded-lg hover:border-indigo-300 cursor-pointer" onClick={() => toggleAddon(addon)}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={selectedAddons.some(a => a.id === addon.id)} readOnly className="accent-indigo-600" />
                      <div><p className="text-sm font-medium">{addon.name}</p>{addon.description && <p className="text-[10px] text-muted-foreground">{addon.description}</p>}</div>
                    </div>
                    <span className="text-sm font-semibold">+${addon.price?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Price Breakdown */}
            <div className="space-y-2 text-sm border-t pt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{quantity}x {item?.title || 'Ticket'}</span>
                <span>${faceValue?.toFixed(2)}</span>
              </div>
              {serviceFee > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Service fee</span>
                  <span>${serviceFee?.toFixed(2)}</span>
                </div>
              )}
              {selectedAddons.map(a => (
                <div key={a.id} className="flex justify-between text-muted-foreground">
                  <span>{a.name}</span>
                  <span>+${a.price?.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className="text-indigo-600">${finalTotal?.toFixed(2)}</span>
              </div>
            </div>

            <Button
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-lg"
              onClick={isAuthenticated ? handleAuthenticatedPay : handleRegisterAndPay}
              disabled={processing || !cardComplete}
              data-testid="checkout-pay-btn"
            >
              {processing ? (
                <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Processing...</span>
              ) : (
                <span className="flex items-center gap-2"><Lock className="w-5 h-5" /> Pay ${finalTotal?.toFixed(2)}</span>
              )}
            </Button>

            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2">
              <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> SSL Secured</span>
              <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> 256-bit encryption</span>
            </div>

            {!isAuthenticated && step === 'payment' && (
              <button className="text-sm text-indigo-600 hover:underline w-full text-center" onClick={() => setStep('account')}>
                <ArrowLeft className="w-3 h-3 inline mr-1" />
                Back to account details
              </button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Checkout Page
export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);
  const [addons, setAddons] = useState([]);

  const itemType = searchParams.get('type') || 'event';
  const itemId = searchParams.get('id');
  const quantity = parseInt(searchParams.get('qty') || '1', 10);

  useEffect(() => {
    const init = async () => {
      if (!itemId) {
        navigate('/events');
        return;
      }
      try {
        // Fetch item details
        let endpoint = itemType === 'event' ? `/events/${itemId}` : `/vouchers/${itemId}`;
        const itemRes = await api.get(endpoint);
        setItem(itemRes.data);

        // Load Stripe
        const configRes = await api.get('/payments/config');
        if (configRes.data?.publishable_key) {
          setStripePromise(loadStripe(configRes.data.publishable_key));
        }
        
        // Fetch add-ons
        try {
          const param = itemType === 'event' ? `event_id=${itemId}` : `experience_id=${itemId}`;
          const addonsRes = await api.get(`/monetization/addons?${param}`);
          setAddons(addonsRes.data.addons || []);
        } catch {}
      } catch (error) {
        toast.error('Failed to load checkout');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [itemId, itemType]);

  if (loading || !item) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const price = item.price || 0;
  const faceValue = price * quantity;
  const serviceFee = faceValue * 0.035 + 1.59 * quantity;
  const totalPrice = faceValue + serviceFee;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Branded Header */}
      <header className="bg-white dark:bg-gray-900 border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight" data-testid="checkout-logo">
            EVENT<span className="text-amber-500">★</span>GOERS
          </Link>
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Lock className="w-4 h-4" />
            Secure Checkout
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back link */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="checkout-back-btn">
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Checkout Form */}
          <div className="lg:col-span-3">
            <h1 className="text-2xl font-bold mb-6">Checkout</h1>
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  item={item}
                  itemType={itemType}
                  quantity={quantity}
                  totalPrice={totalPrice}
                  serviceFee={serviceFee}
                  faceValue={faceValue}
                  addons={addons}
                />
              </Elements>
            ) : (
              <p className="text-muted-foreground">Loading payment form...</p>
            )}
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-2">
            <Card className="sticky top-8 overflow-hidden" data-testid="checkout-order-summary">
              {/* Event Image/Banner */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
                <Badge className="bg-white/20 text-white mb-2">{itemType === 'event' ? 'Event' : 'Experience'}</Badge>
                <h2 className="text-xl font-bold">{item.title}</h2>
              </div>
              <CardContent className="p-5 space-y-4">
                {item.date && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    <span>{formatDate(item.date)}</span>
                  </div>
                )}
                {item.time && (
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <span>{item.time.includes('M') ? item.time : new Date(`2000-01-01T${item.time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                  </div>
                )}
                {(item.venue_name || item.venue_address) && (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-indigo-600" />
                    <span>{item.venue_name || item.venue_address}</span>
                  </div>
                )}

                <div className="border-t pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{quantity}x General Admission</span>
                    <span>${faceValue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Service fee</span>
                    <span>${serviceFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span className="text-indigo-600">${totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-400">
                  <Ticket className="w-4 h-4 inline mr-1" />
                  Tickets are non-refundable. You'll receive a confirmation email after purchase.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
